import React, { useEffect, useRef } from 'react';
import {
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Icon,
} from '@chakra-ui/react';
import { FiSearch } from 'react-icons/fi';

import { connectSearchBox, Configure } from 'react-instantsearch-dom';
import type { SearchBoxProvided } from 'react-instantsearch-core';

const IconClose = ({ ...rest }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...rest}
  >
    <g clipPath="url(#clip0)">
      <rect
        x="24"
        y="24"
        width="24"
        height="24"
        rx="12"
        transform="rotate(-180 24 24)"
        fill="#E4E4F0"
      />
      <path
        d="M15.5 8.5L8.5 15.5"
        stroke="#181823"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.5 8.5L15.5 15.5"
        stroke="#181823"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

type QueryListenerProps = {
  children: (query: string) => JSX.Element | null;
} & SearchBoxProvided;

const QueryListener = connectSearchBox<QueryListenerProps>(
  ({ currentRefinement, children }) => {
    return children(currentRefinement);
  }
);

type SearchInputProps = Pick<SearchBoxProvided, 'currentRefinement' | 'refine'>;

export const SearchInput: React.VFC<SearchInputProps> = ({
  currentRefinement,
  refine,
}) => {
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const current = searchRef.current;
    if (current) {
      current.focus();
    }
  }, []);
  return (
    <InputGroup maxWidth="612px" mt={12}>
      <InputLeftElement pointerEvents="none">
        <Icon as={FiSearch} />
      </InputLeftElement>
      <Input
        value={currentRefinement}
        onChange={(e) => {
          refine(e.target.value);
        }}
        focusBorderColor="custom.primary.600"
        backgroundColor="white"
        ref={searchRef}
      />
      {currentRefinement && (
        <InputRightElement>
          <Icon
            color="custom.neutral.900"
            onClick={() => refine('')}
            aria-label="Clear search query"
            cursor="pointer"
            as={IconClose}
          />
        </InputRightElement>
      )}
    </InputGroup>
  );
};

export const SearchBox: React.VFC<SearchBoxProvided> = ({
  currentRefinement,
  refine,
}) => (
  <>
    <SearchInput currentRefinement={currentRefinement} refine={refine} />
    <QueryListener>
      {(query) => {
        // Only show the suggestions if the query is at least 2 chars long
        const isVisible = query.length > 1;
        return <Configure hitsPerPage={isVisible ? 10 : 0} />;
      }}
    </QueryListener>
  </>
);

export default connectSearchBox(SearchBox);
